import z from 'zod/v4'
import type {
  CampaignKolContract,
  CampaignKolLaborDeclaration,
  CampaignKolPost,
} from '@/types/campaign-case'
import type {
  ApproverType,
  CampaignCooperationDetail,
  CampaignKolStatus,
  ModelCampaignKol,
  OrderStatus,
} from '@/types/mock-api-types'
import type { Nullable } from '@/types/nullable'
import type { FileItem } from '@/types/schema/file-item'
import { accountingInfoSchema } from '@/types/schema/influencer/accounting-info-schema'
import type { IdentityType } from '@/utils/accounting/get-identity-type'
import type { UTMInfo } from '@/utils/utm'

export interface DeleteCampaignKolContractRequest {
  campaignId: number
  campaignKolContractId: number
  campaignKolId: number
}

export enum CampaignKolPaymentStatus {
  Pending = 'pending',
  Processing = 'processing',
  Paid = 'paid',
  NotNeed = 'not-need',
}

export type UpdateCampaignKolPaymentRequest = {
  campaign_id: number
  campaign_kol_ids: number[]
} & (
  | {
      campaign_kol: {
        payment_status:
          | CampaignKolPaymentStatus.NotNeed
          | CampaignKolPaymentStatus.Paid
          | CampaignKolPaymentStatus.Pending
        is_verified: boolean
      }
    }
  | {
      campaign_kol: {
        payment_status: CampaignKolPaymentStatus.Processing
        is_verified: boolean
      }
      request_payment_date: string
      address: string
      juristic_name: string
      bank_account_name?: string
      bank_account_number: string
      bank_branch_code: string
      bank_branch_name: string
      bank_swift_code: string
      bank_name: string
      id_number: string
      identity_type: IdentityType
      phone_number: string
    }
)

export interface LaborDeclarationRequest {
  campaignId: number
  campaignKolId: number
}

export const campaignKolSchema = z.object({
  can_edit_bank_info: z
    .boolean()
    .describe('是否可編輯銀行名稱、分行、帳號等資訊'),
  can_create_labor_declaration: z
    .boolean()
    .describe('是否可建立勞報單（e.g. 公司不需要建立勞報單）'),
  kol_accounting_info: accountingInfoSchema.nullable(),
})

export interface CampaignKol extends z.infer<typeof campaignKolSchema> {
  id: number
  isOffline: boolean
  kolUUID: string
  campaignKolStatus: CampaignKolStatus
  paymentStatus: CampaignKolPaymentStatus
  channelId: string
  kol: ModelCampaignKol
  kolStatInfo: {
    fbAvgEngagementRate3m: Nullable<number>
    fbAvgVideoViewRate3m: Nullable<number>
    fbFollowerCount: Nullable<number>
    followerCount: Nullable<number>
    igAvgEngagementRate3m: Nullable<number>
    igAvgVideoViewRate3m: Nullable<number>
    igFollowerCount: Nullable<number>
    level: string
    ytAvgEngagementRate3m: Nullable<number>
    ytAvgVideoViewRate3m: Nullable<number>
    ytFollowerCount: Nullable<number>
  }
  isCanceled?: boolean
  isAccepted?: boolean | null
  attachments: FileItem[]
  campaignKolPosts: CampaignKolPost[]
  campaignKolLaborDeclaration?: CampaignKolLaborDeclaration
  campaignKolContracts: CampaignKolContract[]
  remark: string | null
  reward: number | null
  startAt: string | null
  endAt: string | null
  cooperationDetail: CampaignCooperationDetail[]
  utmInfo?: UTMInfo
  radarRequestPayment?: {
    requestPaymentDate?: string
  }
  status: CampaignKolStatus
  existsProcessingTransaction: boolean
  /**
   * @description finishedAllContract 合約+勞報單是否皆已簽回
   */
  finishedAllContract: boolean
  /**
   * @description orderPaid 已付款
   */
  orderPaid: boolean
  orderRefundedAt?: string
  orderWithdrewAt?: string
  canWatchReport: boolean
  /** @description kolUploadedPost 網紅已上傳任一貼文，不可取消合作 */
  kolUploadedPost: boolean
  /** @description kolUploadedAllPostsAt 網紅已上傳所有貼文的時間，可撥款條件 */
  kolUploadedAllPostsAt: string | null
  /**
   * @description canCreateTransaction 可以付款的判斷依據，包含是不是在交易中
   */
  canCreateTransaction: boolean
  /**
   * @description 合約、勞報單（如果是個人）皆簽回後產生
   */
  orderStatus: Nullable<OrderStatus>
  orderDepositedAt: string | null
  approverType: ApproverType
}
